import { isLocalStorageAvailable } from '@/js/helpers/functions.js';

// Discovery and legacy share an origin, so Discovery writes the clicked item
// straight to localStorage.entrio_gtm_item (see gtm.ts saveGtmItem). Read it here
// so legacy echoes the exact item Discovery built instead of rebuilding from the DOM.
const readCrossSiteItem = () => {
    if (!isLocalStorageAvailable()) {
        console.warn('[GA4] localStorage is unavailable — cannot read the Discovery hand-off item (entrio_gtm_item); falling back to the DOM-rendered item.');
        return null;
    }
    try {
        const item = JSON.parse(localStorage.getItem('entrio_gtm_item'));
        if (!item || typeof item !== 'object') {
            return null;
        }
        // Discovery serialized undefined -> null (JSON has no undefined). Restore
        // undefined so the echoed item is byte-identical to the Discovery dataLayer
        // item — GA4 must receive the exact same item, not nulls in place of undefined.
        Object.keys(item).forEach((key) => {
            if (item[key] === null) {
                item[key] = undefined;
            }
        });
        return item;
    } catch (e) {
        return null;
    }
};

// Merge the cross-site item over the DOM dataset for the descriptive fields, but
// ONLY when it refers to this same event (item_id === eventId). Ticket-specific
// fields (variant/price/quantity) always come from the DOM; falls back to the DOM
// for any field the cross-site item is missing or when there is no match.
const crossSiteDescriptive = (data) => {
    const xs = readCrossSiteItem();
    const match = xs && String(xs.item_id) === String(data.eventId);
    const pick = (key, fallback) =>
        (match && xs[key] !== undefined && xs[key] !== null && xs[key] !== '') ? xs[key] : fallback;

    return {
        item_name: pick('item_name', data.title),
        item_category: pick('item_category', data.category),
        item_category2: pick('item_category2', (typeof data.category2 !== 'undefined' ? data.category2 : undefined)),
        item_venue: pick('item_venue', data.venue),
        item_location: pick('item_location', data.location),
        item_date: pick('item_date', data.date),
        item_type: pick('item_type', 'Entrio'),
        organizer_id: pick('organizer_id', (typeof data.organizerId !== 'undefined' ? data.organizerId : undefined)),
        provider_id: pick('provider_id', (typeof data.providerId !== 'undefined' ? data.providerId : undefined)),
    };
};

export const initTracking = () => {
    window.track = {};

    // GA4 item parameters (parity with the Discovery dataLayer):
    //  - item_type is constant 'Entrio' here: the legacy sales site only ever
    //    lists/sells events that exist in Entrio.
    //  - item_category2 / organizer_id / provider_id are read from the item data
    //    (dataset attributes or the passed object) and fall back to undefined
    //    until the templates/server provide them. provider_id has no legacy
    //    source yet (TBD with Bero); organizer_id is the event's company_id.

    window.track.trackViewPromotion = function(data) {
        if (typeof dataLayer !== 'undefined') {
            const objectToPush = {
                event: 'view_promotion',
                ecommerce: {}
            };

            objectToPush.ecommerce.items = [];
            objectToPush.ecommerce.items.push({
                item_id: data.eventId,
                item_name: data.title,
                item_category: data.category,
                item_category2: (typeof data.category2 !== 'undefined' ? data.category2 : undefined),
                item_variant: data.variant ?? undefined,
                item_venue: data.venue,
                item_location: data.location,
                item_date: data.date,
                item_type: 'Entrio',
                organizer_id: (typeof data.organizerId !== 'undefined' ? data.organizerId : undefined),
                provider_id: (typeof data.providerId !== 'undefined' ? data.providerId : undefined),
                price: data.price ?? undefined,
                currency: data.currency ?? undefined,
                quantity: undefined,
                item_list_name: undefined,
                item_list_id: undefined,
                index: undefined,
                promotion_id: data.eventId,
                promotion_name: data.title,
                creative_slot: `Home ${data.slideIndex}`,
                creative_name: 'Home Main Slider'
            });

            dataLayer.push(objectToPush);
        }
    }

    window.track.trackSelectPromotion = function(data) {

        const url = data.targetUrl.replace(/^\.\//, '.');

        if (typeof dataLayer !== 'undefined') {
            const objectToPush = {
                event: 'select_promotion',
                ecommerce: {},
                eventTimeout: 2000,
                eventCallback: function() {
                    // window.location = data.targetUrl;
                    window.location = url;
                }
            };

            objectToPush.ecommerce.items = [];
            objectToPush.ecommerce.items.push({
                item_id: data.eventId,
                item_name: data.title,
                item_category: data.category,
                item_category2: (typeof data.category2 !== 'undefined' ? data.category2 : undefined),
                item_variant: data.variant ?? undefined,
                item_venue: data.venue,
                item_location: data.location,
                item_date: data.date,
                item_type: 'Entrio',
                organizer_id: (typeof data.organizerId !== 'undefined' ? data.organizerId : undefined),
                provider_id: (typeof data.providerId !== 'undefined' ? data.providerId : undefined),
                price: data.price ?? undefined,
                currency: data.currency ?? undefined,
                quantity: undefined,
                item_list_name: undefined,
                item_list_id: undefined,
                index: undefined,
                promotion_id: data.eventId,
                promotion_name: data.title,
                creative_slot: `Home ${data.slideIndex}`,
                creative_name: 'Home Main Slider'
            });

            if (isLocalStorageAvailable()) {
                if (Boolean(localStorage.getItem('trackItemList'))) {
                    localStorage.removeItem('trackItemList')
                }

                localStorage.setItem('trackSelectPromotion', JSON.stringify({
                    promotion_id: data.eventId,
                    promotion_name: data.title,
                    creative_slot: `Home ${data.slideIndex}`,
                    creative_name: 'Home Main Slider'
                }));
            }

            dataLayer.push(objectToPush);

            if (!window.google_tag_manager) {
                // window.location = data.targetUrl;
                window.location = url;
            }
        } else {
            // window.location = data.targetUrl;
            window.location = url;
        }
    }

    window.track.trackViewItemList = function(listEl) {
        if (!listEl) {
            return;
        }

        const listItems = Array.from(listEl.querySelectorAll(':scope .js-track-item-list'));

        if (typeof dataLayer !== 'undefined' && listItems.length) {
            const chunkSize = 20; // max number of objects in items list

            for (let i = 0; i < listItems.length; i += chunkSize) {
                const chunk = listItems.slice(i, i + chunkSize);
                const objectToPush = {
                    event: 'view_item_list',
                    ecommerce: {}
                };
                objectToPush.ecommerce.items = [];

                chunk.forEach(c => {
                    const data = c.dataset;

                    objectToPush.ecommerce.items.push({
                        item_id: data.eventId,
                        item_name: data.title,
                        item_category: data.category,
                        item_category2: (typeof data.category2 !== 'undefined' ? data.category2 : undefined),
                        item_variant: data.variant ?? undefined,
                        item_venue: data.venue,
                        item_location: data.location,
                        item_date: data.date,
                        item_type: 'Entrio',
                        organizer_id: (typeof data.organizerId !== 'undefined' ? data.organizerId : undefined),
                        provider_id: (typeof data.providerId !== 'undefined' ? data.providerId : undefined),
                        price: data.price ?? undefined,
                        currency: data.currency ?? undefined,
                        quantity: undefined,
                        item_list_name: data.itemListName ?? undefined,
                        item_list_id: data.itemListId,
                        index: Number(data.index) + 1,
                        promotion_id: undefined,
                        promotion_name: undefined,
                        creative_slot: undefined,
                        creative_name: undefined
                    });
                })

                dataLayer.push(objectToPush);
            }
        }
    }

    window.track.trackSelectItem = function(data) {
        if (typeof dataLayer !== 'undefined') {
            const objectToPush = {
                event: 'select_item',
                ecommerce: {},
                eventTimeout: 2000,
                eventCallback: function() {
                    window.location = data.targetUrl;
                }
            };

            objectToPush.ecommerce.items = [];
            objectToPush.ecommerce.items.push({
                item_id: data.eventId,
                item_name: data.title,
                item_category: data.category,
                item_category2: (typeof data.category2 !== 'undefined' ? data.category2 : undefined),
                item_variant: data.variant ?? undefined,
                item_venue: data.venue,
                item_location: data.location,
                item_date: data.date,
                item_type: 'Entrio',
                organizer_id: (typeof data.organizerId !== 'undefined' ? data.organizerId : undefined),
                provider_id: (typeof data.providerId !== 'undefined' ? data.providerId : undefined),
                price: data.price ?? undefined,
                currency: data.currency ?? undefined,
                quantity: undefined,
                item_list_name: data.itemListName,
                item_list_id: data.itemListId,
                index: Number(data.index) + 1,
                promotion_id: undefined,
                promotion_name: undefined,
                creative_slot: undefined,
                creative_name: undefined
            });

            if (isLocalStorageAvailable()) {
                if (Boolean(localStorage.getItem('trackSelectPromotion'))) {
                    localStorage.removeItem('trackSelectPromotion')
                }

                localStorage.setItem('trackItemList', JSON.stringify({
                    item_list_name: data.itemListName,
                    item_list_id: data.itemListId,
                }));
            }

            dataLayer.push(objectToPush);

            if (!window.google_tag_manager) {
                window.location = data.targetUrl;
            }
        } else {
            window.location = data.targetUrl;
        }
    }

    window.track.trackViewItem = function(listEl) {
        let localStorageItem = {};
        const listItems = Array.from(listEl.querySelectorAll(':scope .js-track-item'));

        if (typeof dataLayer !== 'undefined' && listItems.length) {
            const data = listItems[0].dataset;
            const xs = readCrossSiteItem();

            // Preferred path: echo the exact item Discovery handed off in the cookie,
            // verbatim, so legacy's view_item IS the Discovery item (same promotion/
            // list attribution, index, organizer_id, category — everything).
            if (xs && String(xs.item_id) === String(data.eventId)) {
                dataLayer.push({ event: 'view_item', ecommerce: { items: [xs] } });
                return;
            }

            // Fallback (direct legacy visit, no hand-off): build from the DB-rendered
            // dataset plus any list/promo attribution still in localStorage.
            if (document.referrer && document.referrer.includes('entrio') && isLocalStorageAvailable()) {
                if (Boolean(JSON.parse(localStorage.getItem('trackSelectPromotion')))) {
                    localStorageItem = JSON.parse(localStorage.getItem('trackSelectPromotion'));
                } else if (Boolean(JSON.parse(localStorage.getItem('trackItemList')))) {
                    localStorageItem = JSON.parse(localStorage.getItem('trackItemList'));
                }
            } else if (isLocalStorageAvailable()) {
                if (Boolean(localStorage.getItem('trackSelectPromotion'))) {
                    localStorage.removeItem('trackSelectPromotion')
                }

                if (Boolean(localStorage.getItem('trackItemList'))) {
                    localStorage.removeItem('trackItemList')
                }
            }

            const d = crossSiteDescriptive(data);

            const objectToPush = {
                event: 'view_item',
                ecommerce: {}
            };
            objectToPush.ecommerce.items = [];

            objectToPush.ecommerce.items.push({
                item_id: data.eventId,
                item_name: d.item_name,
                item_category: d.item_category,
                item_category2: d.item_category2,
                item_variant: undefined,
                item_venue: d.item_venue,
                item_location: d.item_location,
                item_date: d.item_date,
                item_type: d.item_type,
                organizer_id: d.organizer_id,
                provider_id: d.provider_id,
                price: undefined,
                currency: undefined,
                quantity: undefined,
                item_list_name: localStorageItem.item_list_name ?? undefined,
                item_list_id: localStorageItem.item_list_id ?? undefined,
                index: Number(data.index) + 1,
                promotion_id: localStorageItem.promotion_id ?? undefined,
                promotion_name: localStorageItem.promotion_name ?? undefined,
                creative_slot: localStorageItem.creative_slot ?? undefined,
                creative_name: localStorageItem.creative_name ?? undefined
            });

            dataLayer.push(objectToPush);
        }
    }

    window.track.addToCart = function(listEl, isRegularShopType) {
        let localStorageItem = {};
        const listItems = Array.from(listEl.querySelectorAll(':scope .js-track-cart-item'));

        const listItemsFiltered = listItems.filter((item) => {
            // Prefer an in-row quantity input: regular shop AND multi-time/scheduler
            // tickets both use .js-track-quantity. Seat-map shops have no quantity
            // input, so fall back to counting selected seats by variant. Keying off
            // the input (not isRegularShopType) makes add_to_cart fire for MULTITIME
            // events, whose rows sendForm() otherwise routes down the seat path.
            const quantityInput = item.querySelector(':scope .js-track-quantity');
            // Variant is a free-text category name; escape \ and " so it can't break the attribute selector
            const variantEscaped = (item.dataset.variant || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
            const quantity = quantityInput
                ? Number(quantityInput.value)
                : Number(document.querySelectorAll(`[data-track-quantity="${variantEscaped}"]`).length / 2);

            if (Boolean(quantity)) {
                return item.dataset.quantity = quantity;
            }
        })

        if (typeof dataLayer !== 'undefined' && listItemsFiltered.length) {
            if (Boolean(JSON.parse(localStorage.getItem('trackSelectPromotion'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackSelectPromotion'));
            } else if (Boolean(JSON.parse(localStorage.getItem('trackItemList'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackItemList'));
            }

            const chunkSize = 20; // max number of objects in items list

            for (let i = 0; i < listItemsFiltered.length; i += chunkSize) {
                const chunk = listItemsFiltered.slice(i, i + chunkSize);
                const objectToPush = {
                    event: 'add_to_cart',
                    ecommerce: {}
                };
                objectToPush.ecommerce.items = [];

                chunk.forEach(c => {
                    const data = c.dataset;
                    const d = crossSiteDescriptive(data);
                    let pricePerTicketWithDiscount = null;
                    let discount = 0;

                    if (data.discountName && data.discountCount && data.discountPerTicket) {
                        const priceWithoutDiscount = Number(data.quantity) * Number(data.price);
                        const qtyForDiscount = Number(data.quantity) > Number(data.discountCount) ? Number(data.discountCount) : Number(data.quantity);
                        const totalDiscount = Number(data.discountPerTicket) * qtyForDiscount;

                        pricePerTicketWithDiscount = (priceWithoutDiscount - totalDiscount) / Number(data.quantity);
                        discount = totalDiscount / Number(data.quantity);
                    }

                    objectToPush.ecommerce.items.push({
                        item_id: data.eventId,
                        item_name: d.item_name,
                        item_category: d.item_category,
                        item_category2: d.item_category2,
                        item_variant: data.variant,
                        item_venue: d.item_venue,
                        item_location: d.item_location,
                        item_date: d.item_date,
                        item_type: d.item_type,
                        organizer_id: d.organizer_id,
                        provider_id: d.provider_id,
                        price: pricePerTicketWithDiscount ?? Number(data.price),
                        discount: discount,
                        coupon: data.discountName ?? undefined,
                        currency: data.currency,
                        quantity: Number(data.quantity),
                        item_list_name: localStorageItem.item_list_name ?? undefined,
                        item_list_id: localStorageItem.item_list_id ?? undefined,
                        index: Number(data.index) + 1,
                        promotion_id: localStorageItem.promotion_id ?? undefined,
                        promotion_name: localStorageItem.promotion_name ?? undefined,
                        creative_slot: localStorageItem.creative_slot ?? undefined,
                        creative_name: localStorageItem.creative_name ?? undefined
                    });
                })
                dataLayer.push(objectToPush);
            }
        }
    }

    window.track.trackViewCart = function(listEl) {
        let localStorageItem = {};
        const listItems = Array.from(listEl.querySelectorAll(':scope .js-track-cart-item'));

        if (typeof dataLayer !== 'undefined' && listItems.length) {
            if (Boolean(JSON.parse(localStorage.getItem('trackSelectPromotion'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackSelectPromotion'));
            } else if (Boolean(JSON.parse(localStorage.getItem('trackItemList'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackItemList'));
            }

            const chunkSize = 20; // max number of objects in items list

            for (let i = 0; i < listItems.length; i += chunkSize) {
                const chunk = listItems.slice(i, i + chunkSize);
                const objectToPush = {
                    event: 'view_cart',
                    ecommerce: {}
                };
                objectToPush.ecommerce.items = [];

                chunk.forEach(c => {
                    const data = c.dataset;
                    const d = crossSiteDescriptive(data);
                    let pricePerTicketWithDiscount = null;
                    let discount = 0;

                    if (data.discountName && data.discountCount && data.discountPerTicket) {
                        const priceWithoutDiscount = Number(data.quantity) * Number(data.price);
                        const qtyForDiscount = Number(data.quantity) > Number(data.discountCount) ? Number(data.discountCount) : Number(data.quantity);
                        const totalDiscount = Number(data.discountPerTicket) * qtyForDiscount;

                        pricePerTicketWithDiscount = (priceWithoutDiscount - totalDiscount) / Number(data.quantity);
                        discount = totalDiscount / Number(data.quantity);
                    }

                    objectToPush.ecommerce.items.push({
                        item_id: data.eventId,
                        item_name: d.item_name,
                        item_category: d.item_category,
                        item_category2: d.item_category2,
                        item_variant: data.variant,
                        item_venue: d.item_venue,
                        item_location: d.item_location,
                        item_date: d.item_date,
                        item_type: d.item_type,
                        organizer_id: d.organizer_id,
                        provider_id: d.provider_id,
                        price: pricePerTicketWithDiscount ?? Number(data.price),
                        discount: discount,
                        coupon: data.discountName ?? undefined,
                        currency: data.currency,
                        quantity: Number(data.quantity),
                        item_list_name: localStorageItem.item_list_name ?? undefined,
                        item_list_id: localStorageItem.item_list_id ?? undefined,
                        index: Number(data.index) + 1,
                        promotion_id: localStorageItem.promotion_id ?? undefined,
                        promotion_name: localStorageItem.promotion_name ?? undefined,
                        creative_slot: localStorageItem.creative_slot ?? undefined,
                        creative_name: localStorageItem.creative_name ?? undefined
                    });
                })

                dataLayer.push(objectToPush);
            }
        }
    }

    window.track.trackBeginCheckout = function(listEl) {
        let localStorageItem = {};
        const listItems = Array.from(listEl.querySelectorAll(':scope .js-track-cart-item'));

        if (typeof dataLayer !== 'undefined' && listItems.length) {
            if (Boolean(JSON.parse(localStorage.getItem('trackSelectPromotion'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackSelectPromotion'));
            } else if (Boolean(JSON.parse(localStorage.getItem('trackItemList'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackItemList'));
            }

            const chunkSize = 20; // max number of objects in items list

            for (let i = 0; i < listItems.length; i += chunkSize) {
                const chunk = listItems.slice(i, i + chunkSize);
                const objectToPush = {
                    event: 'begin_checkout',
                    ecommerce: {}
                };
                objectToPush.ecommerce.items = [];

                chunk.forEach(c => {
                    const data = c.dataset;
                    const d = crossSiteDescriptive(data);
                    let pricePerTicketWithDiscount = null;
                    let discount = 0;

                    if (data.discountName && data.discountCount && data.discountPerTicket) {
                        const priceWithoutDiscount = Number(data.quantity) * Number(data.price);
                        const qtyForDiscount = Number(data.quantity) > Number(data.discountCount) ? Number(data.discountCount) : Number(data.quantity);
                        const totalDiscount = Number(data.discountPerTicket) * qtyForDiscount;

                        pricePerTicketWithDiscount = (priceWithoutDiscount - totalDiscount) / Number(data.quantity);
                        discount = totalDiscount / Number(data.quantity);
                    }

                    objectToPush.ecommerce.items.push({
                        item_id: data.eventId,
                        item_name: d.item_name,
                        item_category: d.item_category,
                        item_category2: d.item_category2,
                        item_variant: data.variant,
                        item_venue: d.item_venue,
                        item_location: d.item_location,
                        item_date: d.item_date,
                        item_type: d.item_type,
                        organizer_id: d.organizer_id,
                        provider_id: d.provider_id,
                        price: pricePerTicketWithDiscount ?? Number(data.price),
                        discount: discount,
                        coupon: data.discountName ?? undefined,
                        currency: data.currency,
                        quantity: Number(data.quantity),
                        item_list_name: localStorageItem.item_list_name ?? undefined,
                        item_list_id: localStorageItem.item_list_id ?? undefined,
                        index: Number(data.index) + 1,
                        promotion_id: localStorageItem.promotion_id ?? undefined,
                        promotion_name: localStorageItem.promotion_name ?? undefined,
                        creative_slot: localStorageItem.creative_slot ?? undefined,
                        creative_name: localStorageItem.creative_name ?? undefined
                    });
                })

                dataLayer.push(objectToPush);
            }
        }
    }

    window.track.trackPurchase = function(data) {
        let localStorageItem = {};

        if (typeof dataLayer !== 'undefined' && data.ecommerce.items) {
            if (Boolean(JSON.parse(localStorage.getItem('trackSelectPromotion'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackSelectPromotion'));
            } else if (Boolean(JSON.parse(localStorage.getItem('trackItemList'))) && isLocalStorageAvailable()) {
                localStorageItem = JSON.parse(localStorage.getItem('trackItemList'));
            }

            const objectToPush = {
                event: 'purchase',
                customer_data: (typeof data.customer_data !== 'undefined' ? data.customer_data : undefined),
                ecommerce: {
                    transaction_id: data.ecommerce.transaction_id,
                    affiliation: data.ecommerce.affiliation,
                    value: Number(data.ecommerce.value),
                    ticket_insurance: Number(data.ecommerce.ticket_insurance),
                    tax: Number(data.ecommerce.tax),
                    shipping: Number(data.ecommerce.shipping),
                    currency: data.ecommerce.currency,
                    login_type: data.ecommerce.login_type,
                    shipping_tier: data.ecommerce.shipping_tier,
                    payment_type: data.ecommerce.payment_type,
                    items: []
                }
            };

            data.ecommerce.items.forEach((item, index) => {
                let pricePerTicketWithDiscount = null;
                let discount = 0;

                const xs = readCrossSiteItem();
                const xsMatch = xs && String(xs.item_id) === String(item.item_id);
                const pick = (key, fallback) =>
                    (xsMatch && xs[key] !== undefined && xs[key] !== null && xs[key] !== '') ? xs[key] : fallback;

                if (item.coupon && item.discounted_tickets_count && item.discounted_price) {
                    const priceWithoutDiscount = Number(item.quantity) * Number(item.price);
                    const qtyForDiscount = Number(item.quantity) > Number(item.discounted_tickets_count) ? Number(item.discounted_tickets_count) : Number(item.quantity);
                    const totalDiscount = Number(item.discounted_price) * qtyForDiscount;

                    pricePerTicketWithDiscount = (priceWithoutDiscount - totalDiscount) / Number(item.quantity);
                    discount = totalDiscount / Number(item.quantity)
                }

                objectToPush.ecommerce.items.push({
                    item_id: item.item_id,
                    item_name: pick('item_name', item.item_name),
                    item_category: pick('item_category', item.item_category),
                    item_category2: pick('item_category2', (typeof item.item_category2 !== 'undefined' ? item.item_category2 : undefined)),
                    item_variant: item.item_variant,
                    item_venue: pick('item_venue', item.item_venue),
                    item_location: pick('item_location', item.item_location),
                    item_date: pick('item_date', item.item_date),
                    item_type: pick('item_type', 'Entrio'),
                    organizer_id: pick('organizer_id', (typeof item.organizer_id !== 'undefined' ? item.organizer_id : undefined)),
                    provider_id: pick('provider_id', (typeof item.provider_id !== 'undefined' ? item.provider_id : undefined)),
                    price: pricePerTicketWithDiscount ?? Number(item.price),
                    discount: discount,
                    coupon: item.coupon ?? undefined,
                    currency: item.currency,
                    quantity: Number(item.quantity),
                    item_list_name: localStorageItem.item_list_name ?? undefined,
                    item_list_id: localStorageItem.item_list_id ?? undefined,
                    index: Number(index) + 1,
                    promotion_id: localStorageItem.promotion_id ?? undefined,
                    promotion_name: localStorageItem.promotion_name ?? undefined,
                    creative_slot: localStorageItem.creative_slot ?? undefined,
                    creative_name: localStorageItem.creative_name ?? undefined
                });
            })

            dataLayer.push(objectToPush);

            if (isLocalStorageAvailable()) {
                if (Boolean(localStorage.getItem('trackSelectPromotion'))) {
                    localStorage.removeItem('trackSelectPromotion')
                }

                if (Boolean(localStorage.getItem('trackItemList'))) {
                    localStorage.removeItem('trackItemList')
                }

                if (Boolean(localStorage.getItem('entrio_gtm_item'))) {
                    localStorage.removeItem('entrio_gtm_item')
                }
            }
        }
    }
};
